import React, { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { cloneDeep, isEmpty, merge } from 'lodash'

import { getStrategyBaseName, strategiesConfig } from '../../utils/strategies.util'
import { formatDate } from '../../utils/helper.util'
import { round } from '../../utils/calc.util'

function TestingStrategies({ strategies, activeStrategy }) {
  const history = useHistory()
  const location = useLocation()
  const [selectedSortValue, setSelectedSortValue] = useState('priorityIndex')
  const [selectedStrategyValue, setSelectedStrategyValue] = useState('')
  const [parsedStrategies, setParsedStrategies] = useState([])
  const [filteredStrategies, setFilteredStrategies] = useState([])
  const [strategiesNames, setStrategiesNames] = useState([])

  const parseStrategies = useCallback((strategies) => {
    const strategiesCopy = cloneDeep(strategies)
    const accountNames = new Map() // Map to store account names and their occurrences
    strategiesCopy.forEach((strategy) => {
      let priorityIndex = 0

      strategy.accounts.forEach((account, index) => {
        if (index > 0) {
          const prevAccount = strategy.accounts[index - 1]

          if (prevAccount.spot.balance > account.spot.balance) {
            priorityIndex++
          }
        }
        const accountFullName = `${getStrategyBaseName(strategy.name)}_${account.name}`
        const openOrdersSum = account.spot?.openOrders.reduce((acc,
          order) => acc + order.quantity * order.stopLossPrice, 0)
        const profit = round(((account.spot.balance + openOrdersSum - account.spot.initialBalance) / account.spot.initialBalance) * 100)

        // Update or create an entry in the accountNames map
        if (accountNames.has(accountFullName)) {
          const existingAccount = accountNames.get(accountFullName)
          existingAccount.totalProfit += profit
          existingAccount.accountSiblingsCount++
          existingAccount.totalPriorityIndex += priorityIndex
        } else {
          accountNames.set(accountFullName, {
            totalProfit: profit,
            accountSiblingsCount: 1,
            totalPriorityIndex: priorityIndex,
          })
        }

        account.ui = {
          initialIndex: index,
          priorityIndex,
          profit,
        }
      })
    })

    strategiesCopy.forEach((strategy) => {
      strategy.accounts.forEach((account, index) => {
        const accountFullName = `${getStrategyBaseName(strategy.name)}_${account.name}`

        merge(account.ui, {
          initialIndex: index,
          totalProfit: accountNames.get(accountFullName).totalProfit,
          accountSiblingsCount: accountNames.get(accountFullName).accountSiblingsCount,
          totalPriorityIndex: accountNames.get(accountFullName).totalPriorityIndex,
        })
      })
    })

    return strategiesCopy
  }, [])

  useEffect(() => {
    if (strategies && isEmpty(strategiesNames)) {
      const strategiesNamesSet = new Set()
      strategies.forEach((strategy) => {
        strategiesNamesSet.add(getStrategyBaseName(strategy.name))
      })

      const strategiesNamesArray = Array.from(strategiesNamesSet)
      setStrategiesNames(strategiesNamesArray)
      setSelectedStrategyValue(selectedStrategyValue || strategiesNamesArray[0])

    }
  }, [selectedStrategyValue, strategies, strategiesNames])

  useEffect(() => {
    if (isEmpty(parsedStrategies) && strategies) {
      setParsedStrategies(parseStrategies(strategies))
    }
  }, [parseStrategies, parsedStrategies, strategies])

  useEffect(() => {
    if (!isEmpty(parsedStrategies) && selectedStrategyValue) {
      const filtered = parsedStrategies.filter(strategy => strategy.name.startsWith(selectedStrategyValue))
        .map(strategy => {
          strategy.accounts.sort((a, b) => {
            if (selectedSortValue === 'priorityIndex') {
              if (b.ui.accountSiblingsCount === a.ui.accountSiblingsCount) {
                return a.ui.totalPriorityIndex - b.ui.totalPriorityIndex
              } else {
                return b.ui.accountSiblingsCount - a.ui.accountSiblingsCount
              }
            }

            if (selectedSortValue === 'totalProfit' || selectedSortValue === 'profit') {
              return b.ui[selectedSortValue] - a.ui[selectedSortValue]
            }

            if (selectedSortValue === 'profitReverse') {
              return a.spot.balance - b.spot.balance
            }

            return b.spot.balance - a.spot.balance
          })

          return strategy
        })

      setFilteredStrategies(filtered)
    }
  }, [parsedStrategies, selectedSortValue, selectedStrategyValue])

  useEffect(() => {
    return () => {
      setSelectedStrategyValue('')
      setParsedStrategies([])
      setFilteredStrategies([])
      setStrategiesNames([])
    }
  }, [strategies])

  return (
    <div>
      <select name="strategiesOptions"
        value={selectedStrategyValue}
        onChange={(event) => { setSelectedStrategyValue(event.target.value) }}>
        {strategiesNames.map((strategyName) => {
          return (<option key={strategyName} value={strategyName}>{strategyName}</option>)
        })}
      </select>
      <select name="sortOptions"
        value={selectedSortValue}
        onChange={(event) => { setSelectedSortValue(event.target.value) }}>
        <option value="priorityIndex">Priority Index</option>
        <option value="totalProfit">Total profit</option>
        <option value="profit">Profit</option>
        <option value="profitReverse">Profit reverse</option>
      </select>
      <div className="cards">
        {filteredStrategies?.length === 0 && <div>No Data</div>}
        {filteredStrategies?.map(strategy => {
          const headerClassnames = classNames(
            'card-header',
            strategy.accounts.some((account) => account.ui.accountSiblingsCount === filteredStrategies.length) &&
            'card-header_highlighted'
          )
          const strategyConfig = strategiesConfig[strategy.strategyType] ?? strategiesConfig[activeStrategy] ?? strategiesConfig.default
          const headers = strategyConfig.generateAccountHeader(strategy)

          return <div key={strategy.name} className="card card-half-width">
            <div className={headerClassnames}>
              <div className="card-row card-row-header">
                <div className="card-cell card-cell_1">
                  <span>{strategy.name}</span>
                </div>
                <div className="card-cell card-cell_1">
                  <span>{formatDate(strategy.startTime, true)} - {formatDate(strategy.endTime, true)}</span>
                </div>
                <div className="card-cell card-cell_1">
                  <span>
                    {round(strategy.startPrice, 3)} - {round(strategy.endPrice, 3)} | {round(strategy.hodl, 0)}%
                  </span>
                </div>
              </div>
            </div>
            <div className="card-content">
              <div className="card-row card-row-header">
                {headers.map(headerCell => (
                  <div key={headerCell.label} className={headerCell.className}>{headerCell.label}</div>
                ))}
              </div>
              {strategy.accounts.map((account) => {
                const accountRowClassnames = classNames(
                  'card-row',
                  account.ui.accountSiblingsCount === filteredStrategies.length && `card-row_highlighted`
                )

                return (
                  <div key={account.name}
                    title={
                      `Index: ${account.ui.initialIndex},\nPriority index: ${account.ui.priorityIndex},\nTotal profit: ${account.ui.totalProfit},\nTotal priority index: ${account.ui.totalPriorityIndex},\nSiblings count: ${account.ui.accountSiblingsCount}`
                    }
                    className={accountRowClassnames}
                    onClick={() => { history.push(`${location.pathname}/${strategy.name}/${account.name}`) }}>
                    {strategyConfig.generateAccountRow(strategy, account).map((bodyCell, index) => {
                      return (
                        <div key={`${headers[index].label}|${bodyCell.value}`}
                          className={bodyCell.className}
                          title={bodyCell.title}
                          onClick={bodyCell.onClick ? bodyCell.onClick : null}>
                          {bodyCell.value}
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
          </div>
        }
        )}
      </div>
    </div>
  )
}

export default TestingStrategies
